import { useLayoutEffect, useState } from "react";

/**
 * The offset a sticky element needs to clear the app header.
 *
 * It cannot be a constant: the header is one height on a phone (where it also
 * carries a secondary nav strip whose presence depends on the role, and a
 * safe-area inset) and 64px at `lg`. Measured from the element itself, so a
 * change in the shell cannot leave a screen's sticky toolbar sliding
 * underneath it. Zero until measured, which costs nothing — nothing is
 * scrolled on first paint.
 *
 * `useLayoutEffect`, not `useEffect`: this number is a `top` on a sticky
 * element, and measuring it after paint puts the toolbar in the wrong place for
 * one frame.
 */
export function useHeaderOffset(): number {
  const [offset, setOffset] = useState(0);

  useLayoutEffect(() => {
    const header = document.querySelector("header");
    if (!header) return;
    const measure = () =>
      setOffset(Math.round(header.getBoundingClientRect().height));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return offset;
}
